<template>
  <PVDialog
    v-model:visible="isVisible"
    :class="[size]"
    :draggable="false"
    :modal="true"
    :pt="{
      root: {
        ...(showHeader
          ? {}
          : {
              style: {
                'padding-top': '2rem',
              },
            }),
      },
      headerActions: {
        style: {
          'margin-left': 'auto',
        },
      },
      content: {
        style: {
          'overflow-y': contentOverflowY,
        },
      },
    }"
    :show-header="showHeader"
    :style="{ ...modalSize, position: 'relative' }"
  >
    <slot></slot>
    <template v-if="displayFooter" #footer>
      <div class="footer-buttons">
        <vf-button
          v-if="displayCancelButton"
          class="cancel-button"
          :disabled="buttonsDisabled || busy"
          :variant="ButtonVariant.TERTIARY"
          @click="handleModalCancel"
        >
          {{ cancelButtonLabel ?? t('generic.cancel') }}
        </vf-button>
        <vf-button
          :busy="busy"
          class="ok-button"
          :disabled="buttonsDisabled || okButtonDisabled || busy"
          :variant="ButtonVariant.PRIMARY"
          @click="handleModalOk"
        >
          {{ okButtonLabel ?? t('generic.ok') }}
        </vf-button>
      </div>
    </template>
  </PVDialog>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n';
  import { ButtonVariant } from '../VfButton/VfButton.types';
  import {
    ModalWrapperProps,
    ModalWrapperExpose,
    ModalSize,
  } from './ModalWrapper.types';
  import PVDialog from 'primevue/dialog';
  import { computed } from 'vue';

  const {
    busy = false,
    buttonsDisabled = false,
    closeOnOkClick = true,
    displayCancelButton = true,
    displayFooter = true,
    okButtonDisabled = false,
    contentOverflowY = 'auto',
    size = ModalSize.M,
    showHeader = true,
  } = defineProps<ModalWrapperProps>();
  const emit = defineEmits<{
    (e: 'ok-button-click'): void;
    (e: 'cancel-button-click'): void;
  }>();
  defineExpose<ModalWrapperExpose>({
    show,
    hide,
  });

  const { t } = useI18n();

  const isVisible = defineModel<boolean>('isVisible', {
    default: false,
  });

  const modalSize = computed(() => {
    switch (size) {
      case ModalSize.M:
        return { width: '500px' };
      case ModalSize.L:
        return { width: '800px' };
      case ModalSize.XL:
        return { width: '1200px' };
      default:
        throw new Error(`Unknown modal size: ${size}`);
    }
  });

  function show() {
    isVisible.value = true;
  }
  function hide() {
    isVisible.value = false;
  }

  function handleModalOk() {
    emit('ok-button-click');
    if (closeOnOkClick) {
      isVisible.value = false;
    }
  }
  function handleModalCancel() {
    emit('cancel-button-click');
    isVisible.value = false;
  }
</script>

<style lang="scss" scoped>
  .footer-buttons {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    .ok-button {
      grid-column: 2/3;
      justify-self: end;
    }
    .cancel-button {
      grid-column: 1/2;
      justify-self: start;
    }
  }
</style>
